/* PlayMaster IA — Offline write queue + sync helpers. */
import { api } from '@/lib/api';
import { enqueue, listQueue, removeFromQueue, bumpQueueTries } from '@/lib/offlineDb';

/**
 * Wrap an API write call (POST / PUT / DELETE) so that when the browser is
 * offline the request is queued in IndexedDB. The caller still gets back a
 * resolved promise with an `{ ok: true, offline: true }` envelope so the UI
 * can show optimistic feedback. When connectivity returns the queue is
 * drained automatically by `drainQueue()`.
 */
export async function writeWithQueue({ method, path, data, optimistic }) {
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  if (!offline) {
    try {
      const res = await api.request({ method, url: path, data });
      return { ok: true, offline: false, data: res.data };
    } catch (e) {
      // If we lose connection mid-flight, still queue.
      const isNetwork = !e?.response;
      if (!isNetwork) throw e;
    }
  }
  await enqueue({ method, path, data });
  return { ok: true, offline: true, optimistic };
}

/** Try to flush all queued operations. Returns counts {sent, failed, remaining}. */
export async function drainQueue() {
  const items = await listQueue().catch(() => []);
  let sent = 0, failed = 0;
  for (const op of items) {
    try {
      await api.request({ method: op.method, url: op.path, data: op.data });
      await removeFromQueue(op.id);
      sent++;
    } catch (e) {
      // Only retry on network errors; drop on 4xx (permanent failure).
      if (e?.response && e.response.status >= 400 && e.response.status < 500) {
        await removeFromQueue(op.id);
      } else {
        await bumpQueueTries(op.id);
      }
      failed++;
    }
  }
  return { sent, failed, remaining: items.length - sent - failed };
}
